/**
 * Amenidades del Portal Condominio (Ticket #1522): tipos del contrato de las RPC `amen_*`,
 * normalización de lo que devuelve PostgREST y la lógica pura de las pantallas (estados, textos,
 * desglose del total, lista de verificación, rejilla de horas en CDMX). Sin Supabase: se prueba
 * sola (`src/lib/__tests__/condominioAmenidades.test.ts`).
 *
 * Reglas de negocio (DISENO §1, cerradas):
 *   - La reserva nace con folio y su cargo ANTES de pagar; el horario queda apartado y, si no se
 *     paga a tiempo, expira sola.
 *   - Quien debe mantenimiento sí reserva, pero confirma pagando el TOTAL = adeudo + reserva (el
 *     pago cubre primero lo más viejo). El servidor calcula montos; aquí solo se muestran.
 *   - Todo en hora de la Ciudad de México: nada de `toISOString` para "hoy".
 */
import { centavos } from "./unidades";
import { hoyCdmx } from "./cobranza";

export const ZONA_CDMX = "America/Mexico_City";

// ---------------------------------------------------------------------------------------------
// Tipos del contrato (CONTRATO.md §1 y §2)
// ---------------------------------------------------------------------------------------------

export type EstadoReserva = "por_pagar" | "confirmada" | "en_curso" | "cerrada" | "cancelada";
export type EstadoPublicacion = "borrador" | "publicada" | "archivada";
export type ModeloCobro = "gratuito" | "por_hora" | "por_reserva";
export type PoliticaCancelacion = "gratis_24h" | "saldo_a_favor" | "sin_reembolso";
export type TipoAmenidad = "sala_juntas" | "cocina" | "sala_tv" | "asador" | "otra";
export type EstadoEspacio = "activo" | "mantenimiento" | "bloqueado";
export type EfectoCancelacion = "sin_cargo" | "saldo_a_favor" | "se_conserva";
export type OrigenReserva = "app" | "portal";
export type TipoCancelacion = "expirada" | "usuario" | "administracion" | "rechazo";
/** Vista de Amenidades > Agenda. */
export type VistaAgenda = "dia" | "semana" | "mes";

export interface EspacioAmen {
  id: number;
  id_amenidad: number;
  nombre: string;
  ubicacion: string | null;
  capacidad_max: number | null;
  estado: EstadoEspacio;
  orden: number;
  activo: boolean;
}

export interface MediaAmen {
  id: number | null;
  tipo: "imagen" | "video";
  storage_path: string;
  orden: number;
  es_portada: boolean;
}

export interface ItemChecklist {
  clave: string;
  ok: boolean;
  texto: string;
}

export interface ReglamentoAmen {
  id: number;
  version: number;
  texto: string;
  storage_path: string | null;
}

export interface AmenidadAmen {
  id: number;
  id_proyecto: number;
  nombre: string;
  descripcion: string | null;
  tipo: TipoAmenidad;
  reservable: boolean;
  estado_publicacion: EstadoPublicacion;
  modelo_cobro: ModeloCobro;
  tarifa: number | null;
  duracion_min_min: number;
  duracion_max_min: number | null;
  capacidad_max: number | null;
  horario_apertura: string | null;
  horario_cierre: string | null;
  limpieza_min: number;
  aviso_cierre_min: number | null;
  politica_cancelacion: PoliticaCancelacion | null;
  anticipacion_min_horas: number;
  anticipacion_max_dias: number | null;
  reglas_texto: string[];
  equipo: string[];
  orden: number;
  portada_path: string | null;
  media: MediaAmen[];
  espacios: EspacioAmen[];
  reglamento: ReglamentoAmen | null;
  checklist: ItemChecklist[];
  publicable: boolean;
  reservas_activas: number;
}

export interface ReservaFila {
  id: number;
  folio: string;
  id_amenidad: number;
  amenidad: string;
  id_espacio: number;
  espacio: string;
  id_cuenta: number;
  unidad: string;
  id_persona: number | null;
  solicitante: string;
  inicio: string;
  fin: string;
  personas: number;
  estado: EstadoReserva;
  origen: OrigenReserva;
  /** `null` si quien consulta no ve montos (solo `leer`). */
  monto: number | null;
  pagada: boolean;
  adeudo_previo: number | null;
  total_para_confirmar: number | null;
  apartado_hasta: string | null;
  confirmada_sin_pago: boolean;
  con_incidencia: boolean;
  checkin_at: string | null;
  checkout_at: string | null;
  created_at: string;
}

export interface ReservaDetalle extends ReservaFila {
  id_reglamento: number | null;
  version_reglamento: number | null;
  acepto_reglamento_at: string | null;
  cancelada_at: string | null;
  tipo_cancelacion: TipoCancelacion | null;
  motivo_cancelacion: string | null;
  efecto_cancelacion: EfectoCancelacion | null;
  confirmada_sin_pago_motivo: string | null;
  checkout_notas: string | null;
  limpieza_ok: boolean | null;
}

export interface PagoReserva {
  monto_reserva: number;
  adeudo_previo: number;
  total: number;
  clabe: string | null;
  banco: string;
  beneficiario: string | null;
  apartado_hasta: string | null;
  mensaje: string | null;
}

export interface DetalleReservaAmen {
  reserva: ReservaDetalle;
  cargo: { id_acuerdo: number; monto: number; pagado: boolean } | null;
  cuenta: { id: number; clabe: string | null; adeudo_previo: number; total_pendiente: number } | null;
  invitados: { id: number; nombre: string }[];
  equipo: { id: number; equipo: string; entregado_at: string | null; devuelto_at: string | null }[];
  incidencias: { id_ticket: number; folio: number | null; etapa: string | null; cerrada: boolean }[];
  eventos: { id: number; tipo: string; texto: string; usuario: string | null; created_at: string }[];
}

export interface KpisReservas {
  por_pagar: number;
  hoy: number;
  monto_pendiente: number | null;
  cobrado_mes: number | null;
  con_incidencia: number;
}

export interface ReservasAmen {
  kpis: KpisReservas;
  reservas: ReservaFila[];
}

export interface ConfigAmen {
  id_proyecto: number;
  reservas_habilitadas: boolean;
  apartado_horas: number;
  recordatorio_horas: number;
  fecha_corte_condovive: string | null;
}

export interface BloqueDisponible {
  hora_inicio: string;
  hora_fin: string;
}

export interface DisponibilidadAmen {
  fecha: string;
  duracion_min: number;
  espacios: { id_espacio: number; nombre: string; estado: EstadoEspacio; bloques: BloqueDisponible[] }[];
}

export interface ReservaAgenda {
  id: number;
  folio: string;
  inicio: string;
  fin: string;
  estado: EstadoReserva;
  unidad: string;
  personas: number;
}

export interface EspacioAgenda {
  id: number;
  nombre: string;
  id_amenidad: number;
  amenidad: string;
  estado: EstadoEspacio;
  horario_apertura: string | null;
  horario_cierre: string | null;
  reservas: ReservaAgenda[];
  bloqueos: { id: number; inicio: string; fin: string; motivo: string }[];
}

export interface AgendaAmen {
  fecha: string;
  espacios: EspacioAgenda[];
  cierres: { id_amenidad: number; amenidad: string; hora_cierre: string; aviso_cierre_min: number | null }[];
  invitados: { id_reserva: number; folio: string; nombre: string }[];
  equipo_prestado: { id_reserva: number; folio: string; equipo: string; entregado_at: string | null }[];
}

export interface ReporteAmen {
  totales: {
    reservas: number;
    confirmadas: number;
    canceladas: number;
    expiradas: number;
    cobrado: number;
    pendiente: number;
    saldo_a_favor_generado: number;
    horas_reservadas: number;
  };
  por_amenidad: { id_amenidad: number; amenidad: string; reservas: number; horas: number; cobrado: number; ocupacion_pct: number }[];
  reservas: (ReservaFila & { efecto_cancelacion: EfectoCancelacion | null })[];
}

export interface MetricasAmen {
  por_canal: { app: number; portal: number };
  minutos_a_confirmar_mediana: number | null;
  expediente_completo_pct: number | null;
  expiradas_pct: number | null;
  equipo_no_devuelto: number;
  incidencias_por_espacio: { id_espacio: number; espacio: string; incidencias: number }[];
  ocupacion_por_espacio: { id_espacio: number; espacio: string; horas: number; ocupacion_pct: number }[];
  por_dia: { fecha: string; reservas: number }[];
}

/** Incidencia ligada a una reserva (`amen_incidencias`, agregado al contrato por el admin). */
export interface IncidenciaAmen {
  id_reserva: number;
  folio_reserva: string;
  amenidad: string;
  espacio: string;
  unidad: string;
  inicio: string | null;
  id_ticket: number;
  folio_ticket: number | null;
  ticket: string | null;
  etapa: string | null;
  cerrada: boolean;
  created_at: string | null;
}

/** Titulares de una unidad para reservar en mostrador (`amen_titulares_cuenta`). */
export interface TitularesCuenta {
  id_cuenta: number;
  unidad: string;
  adeudo: number;
  titulares: { id_persona: number; nombre: string }[];
}

export interface EfectoCancelacionInfo {
  efecto: EfectoCancelacion;
  monto: number;
  texto: string;
}

// ---------------------------------------------------------------------------------------------
// Normalización (PostgREST manda `numeric` como texto y puede omitir llaves)
// ---------------------------------------------------------------------------------------------

type Crudo = Record<string, unknown>;
const num = (v: unknown, def = 0): number => (v == null || v === "" ? def : Number(v) || 0);
const numN = (v: unknown): number | null => (v == null || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const str = (v: unknown, def = ""): string => (v == null ? def : String(v));
const strN = (v: unknown): string | null => (v == null || v === "" ? null : String(v));
const lista = (v: unknown): Crudo[] => (Array.isArray(v) ? (v as Crudo[]) : []);
const textos = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : []);
/** `'08:00:00'` → `'08:00'`. */
const hhmm = (v: unknown): string | null => (v == null || v === "" ? null : String(v).slice(0, 5));

export function normalizarEspacio(f: Crudo): EspacioAmen {
  return {
    id: num(f.id),
    id_amenidad: num(f.id_amenidad),
    nombre: str(f.nombre),
    ubicacion: strN(f.ubicacion),
    capacidad_max: numN(f.capacidad_max),
    estado: (str(f.estado, "activo") as EstadoEspacio),
    orden: num(f.orden),
    activo: f.activo !== false,
  };
}

export function normalizarAmenidad(f: Crudo): AmenidadAmen {
  const reglamento = f.reglamento as Crudo | null | undefined;
  return {
    id: num(f.id),
    id_proyecto: num(f.id_proyecto),
    nombre: str(f.nombre),
    descripcion: strN(f.descripcion),
    tipo: (str(f.tipo, "otra") as TipoAmenidad),
    reservable: f.reservable !== false,
    estado_publicacion: (str(f.estado_publicacion, "borrador") as EstadoPublicacion),
    modelo_cobro: (str(f.modelo_cobro, "gratuito") as ModeloCobro),
    tarifa: numN(f.tarifa),
    duracion_min_min: num(f.duracion_min_min, 60) || 60,
    duracion_max_min: numN(f.duracion_max_min),
    capacidad_max: numN(f.capacidad_max),
    horario_apertura: hhmm(f.horario_apertura),
    horario_cierre: hhmm(f.horario_cierre),
    limpieza_min: num(f.limpieza_min, 30),
    aviso_cierre_min: numN(f.aviso_cierre_min),
    politica_cancelacion: (strN(f.politica_cancelacion) as PoliticaCancelacion | null),
    anticipacion_min_horas: num(f.anticipacion_min_horas),
    anticipacion_max_dias: numN(f.anticipacion_max_dias),
    reglas_texto: textos(f.reglas_texto),
    equipo: textos(f.equipo),
    orden: num(f.orden),
    portada_path: strN(f.portada_path),
    media: lista(f.media).map((m) => ({
      id: numN(m.id),
      tipo: m.tipo === "video" ? "video" : "imagen",
      storage_path: str(m.storage_path),
      orden: num(m.orden),
      es_portada: m.es_portada === true,
    })),
    espacios: lista(f.espacios).map(normalizarEspacio).sort((a, b) => a.orden - b.orden || a.id - b.id),
    reglamento: reglamento && reglamento.id != null
      ? { id: num(reglamento.id), version: num(reglamento.version, 1), texto: str(reglamento.texto), storage_path: strN(reglamento.storage_path) }
      : null,
    checklist: lista(f.checklist).map((c) => ({ clave: str(c.clave), ok: c.ok === true, texto: str(c.texto) })),
    publicable: f.publicable === true,
    reservas_activas: num(f.reservas_activas),
  };
}

export function normalizarReserva(f: Crudo): ReservaFila {
  return {
    id: num(f.id),
    folio: str(f.folio),
    id_amenidad: num(f.id_amenidad),
    amenidad: str(f.amenidad),
    id_espacio: num(f.id_espacio),
    espacio: str(f.espacio),
    id_cuenta: num(f.id_cuenta),
    unidad: str(f.unidad),
    id_persona: numN(f.id_persona),
    solicitante: str(f.solicitante),
    inicio: str(f.inicio),
    fin: str(f.fin),
    personas: num(f.personas),
    estado: (str(f.estado, "por_pagar") as EstadoReserva),
    origen: f.origen === "app" ? "app" : "portal",
    monto: numN(f.monto),
    pagada: f.pagada === true,
    adeudo_previo: numN(f.adeudo_previo),
    total_para_confirmar: numN(f.total_para_confirmar),
    apartado_hasta: strN(f.apartado_hasta),
    confirmada_sin_pago: f.confirmada_sin_pago === true,
    con_incidencia: f.con_incidencia === true,
    checkin_at: strN(f.checkin_at),
    checkout_at: strN(f.checkout_at),
    created_at: str(f.created_at),
  };
}

export function normalizarPagoReserva(f: Crudo | null | undefined): PagoReserva | null {
  if (!f) return null;
  return {
    monto_reserva: num(f.monto_reserva),
    adeudo_previo: num(f.adeudo_previo),
    total: num(f.total),
    clabe: strN(f.clabe),
    banco: str(f.banco, "STP"),
    beneficiario: strN(f.beneficiario),
    apartado_hasta: strN(f.apartado_hasta),
    mensaje: strN(f.mensaje),
  };
}

export function normalizarReservas(d: Crudo | null | undefined): ReservasAmen {
  const k = (d?.kpis ?? {}) as Crudo;
  return {
    kpis: {
      por_pagar: num(k.por_pagar),
      hoy: num(k.hoy),
      monto_pendiente: numN(k.monto_pendiente),
      cobrado_mes: numN(k.cobrado_mes),
      con_incidencia: num(k.con_incidencia),
    },
    reservas: lista(d?.reservas).map(normalizarReserva),
  };
}

export function normalizarDetalle(d: Crudo): DetalleReservaAmen {
  const r = (d.reserva ?? {}) as Crudo;
  const cargo = d.cargo as Crudo | null | undefined;
  const cuenta = d.cuenta as Crudo | null | undefined;
  return {
    reserva: {
      ...normalizarReserva(r),
      id_reglamento: numN(r.id_reglamento),
      version_reglamento: numN(r.version_reglamento),
      acepto_reglamento_at: strN(r.acepto_reglamento_at),
      cancelada_at: strN(r.cancelada_at),
      tipo_cancelacion: strN(r.tipo_cancelacion) as TipoCancelacion | null,
      motivo_cancelacion: strN(r.motivo_cancelacion),
      efecto_cancelacion: strN(r.efecto_cancelacion) as EfectoCancelacion | null,
      confirmada_sin_pago_motivo: strN(r.confirmada_sin_pago_motivo),
      checkout_notas: strN(r.checkout_notas),
      limpieza_ok: r.limpieza_ok == null ? null : r.limpieza_ok === true,
    },
    cargo: cargo ? { id_acuerdo: num(cargo.id_acuerdo), monto: num(cargo.monto), pagado: cargo.pagado === true } : null,
    cuenta: cuenta
      ? { id: num(cuenta.id), clabe: strN(cuenta.clabe), adeudo_previo: num(cuenta.adeudo_previo), total_pendiente: num(cuenta.total_pendiente) }
      : null,
    invitados: lista(d.invitados).map((i) => ({ id: num(i.id), nombre: str(i.nombre) })),
    equipo: lista(d.equipo).map((e) => ({ id: num(e.id), equipo: str(e.equipo), entregado_at: strN(e.entregado_at), devuelto_at: strN(e.devuelto_at) })),
    incidencias: lista(d.incidencias).map((i) => ({ id_ticket: num(i.id_ticket), folio: numN(i.folio), etapa: strN(i.etapa), cerrada: i.cerrada === true })),
    eventos: lista(d.eventos)
      .map((e) => ({ id: num(e.id), tipo: str(e.tipo), texto: str(e.texto), usuario: strN(e.usuario), created_at: str(e.created_at) }))
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
  };
}

export function normalizarConfig(d: Crudo, idProyecto: number): ConfigAmen {
  return {
    id_proyecto: num(d.id_proyecto, idProyecto) || idProyecto,
    reservas_habilitadas: d.reservas_habilitadas === true,
    apartado_horas: num(d.apartado_horas, 24) || 24,
    recordatorio_horas: num(d.recordatorio_horas, 24) || 24,
    fecha_corte_condovive: strN(d.fecha_corte_condovive)?.slice(0, 10) ?? null,
  };
}

export function normalizarDisponibilidad(d: Crudo): DisponibilidadAmen {
  return {
    fecha: str(d.fecha).slice(0, 10),
    duracion_min: num(d.duracion_min),
    espacios: lista(d.espacios).map((e) => ({
      id_espacio: num(e.id_espacio),
      nombre: str(e.nombre),
      estado: (str(e.estado, "activo") as EstadoEspacio),
      bloques: lista(e.bloques).map((b) => ({ hora_inicio: hhmm(b.hora_inicio) ?? "", hora_fin: hhmm(b.hora_fin) ?? "" })),
    })),
  };
}

export function normalizarAgenda(d: Crudo): AgendaAmen {
  return {
    fecha: str(d.fecha).slice(0, 10),
    espacios: lista(d.espacios).map((e) => ({
      id: num(e.id),
      nombre: str(e.nombre),
      id_amenidad: num(e.id_amenidad),
      amenidad: str(e.amenidad),
      estado: (str(e.estado, "activo") as EstadoEspacio),
      horario_apertura: hhmm(e.horario_apertura),
      horario_cierre: hhmm(e.horario_cierre),
      reservas: lista(e.reservas).map((r) => ({
        id: num(r.id), folio: str(r.folio), inicio: str(r.inicio), fin: str(r.fin),
        estado: (str(r.estado) as EstadoReserva), unidad: str(r.unidad), personas: num(r.personas),
      })),
      bloqueos: lista(e.bloqueos).map((b) => ({ id: num(b.id), inicio: str(b.inicio), fin: str(b.fin), motivo: str(b.motivo) })),
    })),
    cierres: lista(d.cierres).map((c) => ({
      id_amenidad: num(c.id_amenidad), amenidad: str(c.amenidad), hora_cierre: hhmm(c.hora_cierre) ?? "", aviso_cierre_min: numN(c.aviso_cierre_min),
    })),
    invitados: lista(d.invitados).map((i) => ({ id_reserva: num(i.id_reserva), folio: str(i.folio), nombre: str(i.nombre) })),
    equipo_prestado: lista(d.equipo_prestado).map((e) => ({
      id_reserva: num(e.id_reserva), folio: str(e.folio), equipo: str(e.equipo), entregado_at: strN(e.entregado_at),
    })),
  };
}

export function normalizarReporte(d: Crudo): ReporteAmen {
  const t = (d.totales ?? {}) as Crudo;
  return {
    totales: {
      reservas: num(t.reservas),
      confirmadas: num(t.confirmadas),
      canceladas: num(t.canceladas),
      expiradas: num(t.expiradas),
      cobrado: num(t.cobrado),
      pendiente: num(t.pendiente),
      saldo_a_favor_generado: num(t.saldo_a_favor_generado),
      horas_reservadas: num(t.horas_reservadas),
    },
    por_amenidad: lista(d.por_amenidad).map((a) => ({
      id_amenidad: num(a.id_amenidad), amenidad: str(a.amenidad), reservas: num(a.reservas), horas: num(a.horas),
      cobrado: num(a.cobrado), ocupacion_pct: num(a.ocupacion_pct),
    })),
    reservas: lista(d.reservas).map((r) => ({ ...normalizarReserva(r), efecto_cancelacion: strN(r.efecto_cancelacion) as EfectoCancelacion | null })),
  };
}

export function normalizarMetricas(d: Crudo): MetricasAmen {
  const c = (d.por_canal ?? {}) as Crudo;
  return {
    por_canal: { app: num(c.app), portal: num(c.portal) },
    minutos_a_confirmar_mediana: numN(d.minutos_a_confirmar_mediana),
    expediente_completo_pct: numN(d.expediente_completo_pct),
    expiradas_pct: numN(d.expiradas_pct),
    equipo_no_devuelto: num(d.equipo_no_devuelto),
    incidencias_por_espacio: lista(d.incidencias_por_espacio).map((e) => ({ id_espacio: num(e.id_espacio), espacio: str(e.espacio), incidencias: num(e.incidencias) })),
    ocupacion_por_espacio: lista(d.ocupacion_por_espacio).map((e) => ({ id_espacio: num(e.id_espacio), espacio: str(e.espacio), horas: num(e.horas), ocupacion_pct: num(e.ocupacion_pct) })),
    por_dia: lista(d.por_dia).map((p) => ({ fecha: str(p.fecha).slice(0, 10), reservas: num(p.reservas) })),
  };
}

export function normalizarIncidencia(f: Crudo): IncidenciaAmen {
  return {
    id_reserva: num(f.id_reserva),
    folio_reserva: str(f.folio_reserva),
    amenidad: str(f.amenidad),
    espacio: str(f.espacio),
    unidad: str(f.unidad),
    inicio: strN(f.inicio),
    id_ticket: num(f.id_ticket),
    folio_ticket: numN(f.folio_ticket),
    ticket: strN(f.ticket),
    etapa: strN(f.etapa),
    cerrada: f.cerrada === true,
    created_at: strN(f.created_at),
  };
}

export function normalizarTitulares(d: Crudo): TitularesCuenta {
  return {
    id_cuenta: num(d.id_cuenta),
    unidad: str(d.unidad),
    adeudo: num(d.adeudo),
    titulares: lista(d.titulares).map((t) => ({ id_persona: num(t.id_persona), nombre: str(t.nombre) })),
  };
}

// ---------------------------------------------------------------------------------------------
// Catálogos de textos y tonos
// ---------------------------------------------------------------------------------------------

export type TonoEstado = "success" | "warning" | "danger" | "info" | "neutral" | "blue";

export const ESTADO_RESERVA: Record<EstadoReserva, { label: string; tono: TonoEstado; descripcion: string }> = {
  por_pagar: { label: "Por pagar", tono: "warning", descripcion: "Horario apartado: se confirma sola cuando su cargo queda pagado." },
  confirmada: { label: "Confirmada", tono: "success", descripcion: "Pagada (o sin costo): el espacio está reservado." },
  en_curso: { label: "En curso", tono: "blue", descripcion: "Con check-in: el residente está usando el espacio." },
  cerrada: { label: "Cerrada", tono: "neutral", descripcion: "Con check-out o cerrada sin check-in al terminar." },
  cancelada: { label: "Cancelada", tono: "danger", descripcion: "Cancelada o expirada: el horario quedó libre." },
};

export const ESTADO_PUBLICACION: Record<EstadoPublicacion, { label: string; tono: TonoEstado }> = {
  borrador: { label: "Borrador", tono: "warning" },
  publicada: { label: "Publicada", tono: "success" },
  archivada: { label: "Archivada", tono: "neutral" },
};

export const ESTADO_ESPACIO: Record<EstadoEspacio, { label: string; tono: TonoEstado }> = {
  activo: { label: "Activo", tono: "success" },
  mantenimiento: { label: "En mantenimiento", tono: "warning" },
  bloqueado: { label: "Bloqueado", tono: "danger" },
};

export const TIPO_AMENIDAD: Record<TipoAmenidad, string> = {
  sala_juntas: "Sala de juntas",
  cocina: "Cocina",
  sala_tv: "Sala de TV",
  asador: "Asador",
  otra: "Otra",
};

export const MODELO_COBRO: Record<ModeloCobro, string> = {
  gratuito: "Sin costo",
  por_hora: "Por hora",
  por_reserva: "Por reservación",
};

export const POLITICA_CANCELACION: Record<PoliticaCancelacion, { label: string; texto: string }> = {
  gratis_24h: {
    label: "Gratis hasta 24 h antes",
    texto: "Si cancela con más de 24 h de anticipación, lo pagado queda como saldo a favor; después se conserva el cobro.",
  },
  saldo_a_favor: {
    label: "Saldo a favor",
    texto: "Al cancelar, lo pagado queda como saldo a favor en la cuenta de la unidad.",
  },
  sin_reembolso: {
    label: "Sin reembolso",
    texto: "Al cancelar se conserva el cobro.",
  },
};

export const EFECTO_CANCELACION: Record<EfectoCancelacion, string> = {
  sin_cargo: "Sin cargo: se desactiva el cargo pendiente",
  saldo_a_favor: "Lo pagado queda como saldo a favor",
  se_conserva: "Se conserva el cobro",
};

export const TIPO_CANCELACION: Record<TipoCancelacion, string> = {
  expirada: "Expiró sin pago",
  usuario: "La canceló el residente",
  administracion: "La canceló la administración",
  rechazo: "Rechazada",
};

export const ORIGEN_RESERVA: Record<OrigenReserva, string> = { app: "App de Clientes", portal: "Mostrador" };

/** Mensajes de los códigos de error del contrato (§7), por si la RPC no manda DETAIL. */
export const ERRORES_AMEN: Record<string, string> = {
  sin_permiso: "No tienes permiso para esta acción.",
  no_encontrada: "No encontramos esa reservación.",
  no_titular: "La persona no es titular de la unidad.",
  amenidad_no_disponible: "La amenidad no está publicada para reservar.",
  espacio_no_disponible: "El espacio no está disponible.",
  fuera_de_horario: "El horario queda fuera del horario de la amenidad.",
  duracion_invalida: "La duración no es válida para esta amenidad.",
  excede_capacidad: "Son más personas de las que caben en el espacio.",
  anticipacion: "El horario no respeta la anticipación de la amenidad.",
  choque_horario: "Ese horario se acaba de ocupar. Elige otro.",
  bloqueado: "El espacio está bloqueado en ese horario.",
  reglamento_no_aceptado: "Falta aceptar el reglamento vigente.",
  reglamento_desactualizado: "El reglamento cambió: vuelve a aceptarlo.",
  estado_invalido: "La reservación ya cambió de estado. Recarga para ver la versión actual.",
  amenidad_incompleta: "La amenidad tiene pendientes en su lista de verificación.",
  motivo_requerido: "Escribe el motivo.",
  choque_reserva: "El bloqueo pisa una reservación activa.",
  reservas_apagadas: "Las reservaciones están apagadas en este condominio.",
};

// ---------------------------------------------------------------------------------------------
// Fechas y horas en la Ciudad de México
// ---------------------------------------------------------------------------------------------

const FMT_HORA = new Intl.DateTimeFormat("en-GB", { timeZone: ZONA_CDMX, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const FMT_FECHA = new Intl.DateTimeFormat("en-CA", { timeZone: ZONA_CDMX });
const p2 = (n: number) => String(n).padStart(2, "0");

/** Hora `HH:MM` de un instante en CDMX. */
export const horaCdmx = (iso: string | Date): string => FMT_HORA.format(typeof iso === "string" ? new Date(iso) : iso);
/** Fecha `YYYY-MM-DD` de un instante en CDMX. */
export const fechaCdmx = (iso: string | Date): string => FMT_FECHA.format(typeof iso === "string" ? new Date(iso) : iso);
/** `'16:30'` → 990. */
export const minutosDeHora = (h: string): number => {
  const [hh, mm] = h.split(":").map(Number);
  return (hh || 0) * 60 + (mm || 0);
};
/** 990 → `'16:30'`; 1440 → `'24:00'`. */
export const horaDeMinutos = (m: number): string => `${p2(Math.floor(m / 60))}:${p2(m % 60)}`;
/** Minuto del día (0-1439) de un instante en CDMX. */
export const minutosCdmx = (iso: string | Date): number => minutosDeHora(horaCdmx(iso));

/** Fecha y minuto de "ahora" en CDMX. */
export function ahoraCdmx(ahora = new Date()): { fecha: string; minutos: number } {
  return { fecha: hoyCdmx(ahora), minutos: minutosCdmx(ahora) };
}

/**
 * Instante ISO de una fecha y hora de CDMX (`'2026-11-03'`, `'16:00'` → `'2026-11-03T16:00:00-06:00'`).
 * La Ciudad de México no tiene horario de verano desde octubre de 2022: el desfase es siempre -06:00.
 */
export const isoCdmx = (fecha: string, hora: string): string => `${fecha.slice(0, 10)}T${hora.slice(0, 5)}:00-06:00`;

/** Suma días a una fecha `YYYY-MM-DD` sin pasar por la zona del navegador. */
export function sumarDias(fecha: string, dias: number): string {
  const [y, m, d] = fecha.split("-").map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, d! + dias));
  return `${t.getUTCFullYear()}-${p2(t.getUTCMonth() + 1)}-${p2(t.getUTCDate())}`;
}

/** Lunes de la semana de `fecha` (`YYYY-MM-DD`). */
export function lunesDe(fecha: string): string {
  const [y, m, d] = fecha.split("-").map(Number);
  const dow = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay(); // 0 domingo
  return sumarDias(fecha, dow === 0 ? -6 : 1 - dow);
}

const FMT_FECHA_LARGA = new Intl.DateTimeFormat("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const FMT_FECHA_CORTA = new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const deFecha = (fecha: string) => new Date(`${fecha.slice(0, 10)}T12:00:00Z`);

/** `'2026-11-03'` → "martes 3 de noviembre de 2026". */
export const fechaLarga = (fecha: string): string => FMT_FECHA_LARGA.format(deFecha(fecha));
/** `'2026-11-03'` → "03 nov 2026". */
export const fechaCorta = (fecha: string): string => FMT_FECHA_CORTA.format(deFecha(fecha)).replace(".", "");
/** Instante → "03 nov 2026 - 16:00" en CDMX. */
export const fechaHoraCorta = (iso: string): string => `${fechaCorta(fechaCdmx(iso))} - ${horaCdmx(iso)}`;
/** Instante → "03 nov 16:00" en CDMX (celdas angostas: sin año). */
export const fechaHoraBreve = (iso: string): string => `${fechaCorta(fechaCdmx(iso)).slice(0, -5)} ${horaCdmx(iso)}`;
/** Rango de una reserva: "16:00-18:00". */
export const rangoHoras = (inicio: string, fin: string): string => `${horaCdmx(inicio)}-${horaCdmx(fin)}`;

/** 90 → "1 h 30 min"; 120 → "2 h"; 30 → "30 min". */
export function duracionTexto(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Minutos entre dos instantes. */
export const minutosEntre = (inicio: string, fin: string): number =>
  Math.round((new Date(fin).getTime() - new Date(inicio).getTime()) / 60000);

/**
 * Inicios posibles en la rejilla de una amenidad: desde la apertura, cada `pasoMin`, mientras el
 * bloque de `duracionMin` termine antes del cierre (no se pasa del cierre). La disponibilidad
 * real (choques, limpieza, bloqueos) la decide el servidor; esto arma la rejilla y valida horas.
 */
export function rejillaInicios(apertura: string, cierre: string, pasoMin: number, duracionMin = pasoMin): string[] {
  const ini = minutosDeHora(apertura);
  let fin = minutosDeHora(cierre);
  if (fin <= ini) fin += 24 * 60; // cierre 00:00 = medianoche
  if (pasoMin <= 0 || duracionMin <= 0) return [];
  const out: string[] = [];
  for (let m = ini; m + duracionMin <= fin; m += pasoMin) out.push(horaDeMinutos(m % (24 * 60)));
  return out;
}

/** Duraciones que se ofrecen: del bloque mínimo a la duración máxima, de bloque en bloque. */
export function duracionesPermitidas(a: Pick<AmenidadAmen, "duracion_min_min" | "duracion_max_min">): number[] {
  const paso = a.duracion_min_min > 0 ? a.duracion_min_min : 60;
  const max = a.duracion_max_min && a.duracion_max_min >= paso ? a.duracion_max_min : paso;
  const out: number[] = [];
  for (let d = paso; d <= max; d += paso) out.push(d);
  return out;
}

/** Ventana de horas (en minutos, a la hora cerrada) que cubre los horarios de los espacios. */
export function ventanaAgenda(horarios: { horario_apertura: string | null; horario_cierre: string | null }[]): { inicio: number; fin: number } {
  let inicio = Infinity;
  let fin = -Infinity;
  for (const h of horarios) {
    if (h.horario_apertura) inicio = Math.min(inicio, minutosDeHora(h.horario_apertura));
    if (h.horario_cierre) {
      const c = minutosDeHora(h.horario_cierre);
      fin = Math.max(fin, c === 0 ? 24 * 60 : c);
    }
  }
  if (!Number.isFinite(inicio)) inicio = 8 * 60;
  if (!Number.isFinite(fin) || fin <= inicio) fin = 23 * 60;
  return { inicio: Math.floor(inicio / 60) * 60, fin: Math.min(24 * 60, Math.ceil(fin / 60) * 60) };
}

/** Rango por defecto de Reservaciones: últimos 30 días y próximos 60 (CDMX). */
export function rangoReservasPorDefecto(hoy = hoyCdmx()): { desde: string; hasta: string } {
  return { desde: sumarDias(hoy, -30), hasta: sumarDias(hoy, 60) };
}

// ---------------------------------------------------------------------------------------------
// Dinero: estimado, desglose del total y textos de pago
// ---------------------------------------------------------------------------------------------

/**
 * Monto que el servidor cobrará (solo para la vista previa del mostrador): por hora = tarifa por
 * horas (fracción incluida), por reservación = tarifa, sin costo = 0. El cargo real lo calcula
 * `amen__crear_reserva`.
 */
export function montoEstimado(a: Pick<AmenidadAmen, "modelo_cobro" | "tarifa">, duracionMin: number): number {
  if (a.modelo_cobro === "gratuito" || !a.tarifa) return 0;
  if (a.modelo_cobro === "por_reserva") return a.tarifa;
  return Math.round(centavos(a.tarifa) * (duracionMin / 60)) / 100;
}

export interface DesgloseTotal {
  adeudo: number;
  reserva: number;
  total: number;
  /** Debe más de cero de mantenimiento: el mensaje pide ponerse al corriente. */
  conAdeudo: boolean;
}

/**
 * Total para confirmar (respuesta de Ramón, 4-nov): adeudo vencido de la cuenta + la reserva si
 * su cargo no está pagado. En centavos para no dejar $0.01 fantasma.
 */
export function desgloseTotal(adeudo: number | null | undefined, monto: number | null | undefined, pagada = false): DesgloseTotal {
  const a = Math.max(0, centavos(adeudo ?? 0));
  const r = pagada ? 0 : Math.max(0, centavos(monto ?? 0));
  return { adeudo: a / 100, reserva: r / 100, total: (a + r) / 100, conAdeudo: a > 0 };
}

const MXN = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
export const pesos = (n: number): string => MXN.format(n || 0);

/** "Adeudo de mantenimiento $3,600.00 + Reserva $700.00 = Total $4,300.00". */
export function textoDesglose(d: DesgloseTotal): string {
  if (!d.conAdeudo) return `Total ${pesos(d.total)}`;
  return `Adeudo de mantenimiento ${pesos(d.adeudo)} + Reserva ${pesos(d.reserva)} = Total ${pesos(d.total)}`;
}

/** Línea de pago de la tabla: qué pasa con el cargo de la reserva. */
export function textoPago(f: Pick<ReservaFila, "monto" | "pagada" | "estado" | "confirmada_sin_pago" | "apartado_hasta">): { texto: string; detalle: string | null; tono: TonoEstado } {
  if (f.monto === 0) return { texto: "Sin costo", detalle: null, tono: "neutral" };
  if (f.pagada) return { texto: "Pagado", detalle: "Aplicado por STP", tono: "success" };
  if (f.estado === "cancelada") return { texto: "Sin cobro", detalle: "Cargo desactivado", tono: "neutral" };
  if (f.confirmada_sin_pago) return { texto: "Pendiente", detalle: "Confirmada sin pago", tono: "warning" };
  if (f.estado === "por_pagar") {
    return { texto: "Por pagar", detalle: f.apartado_hasta ? `Vence ${fechaHoraBreve(f.apartado_hasta)}` : null, tono: "warning" };
  }
  return { texto: "Pendiente", detalle: null, tono: "warning" };
}

// ---------------------------------------------------------------------------------------------
// Pestañas, búsqueda y prioridades de Reservaciones
// ---------------------------------------------------------------------------------------------

export type PestanaReservas = "todas" | "por_pagar" | "hoy" | "proximas" | "en_curso" | "con_incidencia" | "historial";

export const PESTANAS_RESERVAS: { id: PestanaReservas; label: string; descripcion: string }[] = [
  { id: "todas", label: "Todas", descripcion: "Todas las reservaciones del periodo." },
  { id: "por_pagar", label: "Por pagar", descripcion: "Con el horario apartado, esperando el pago." },
  { id: "hoy", label: "Hoy", descripcion: "Las que empiezan hoy (CDMX)." },
  { id: "proximas", label: "Próximas", descripcion: "Confirmadas de mañana en adelante." },
  { id: "en_curso", label: "En curso", descripcion: "Con check-in, sin check-out." },
  { id: "con_incidencia", label: "Con incidencia", descripcion: "Con un ticket ligado." },
  { id: "historial", label: "Historial", descripcion: "Cerradas y canceladas." },
];

export function enPestana(f: ReservaFila, pestana: PestanaReservas, hoy: string): boolean {
  const dia = fechaCdmx(f.inicio);
  switch (pestana) {
    case "todas": return true;
    case "por_pagar": return f.estado === "por_pagar";
    case "hoy": return dia === hoy && f.estado !== "cancelada";
    case "proximas": return dia > hoy && (f.estado === "confirmada" || f.estado === "por_pagar");
    case "en_curso": return f.estado === "en_curso";
    case "con_incidencia": return f.con_incidencia;
    case "historial": return f.estado === "cerrada" || f.estado === "cancelada";
  }
}

export function conteosPestanas(filas: ReservaFila[], hoy: string): Record<PestanaReservas, number> {
  const c = { todas: 0, por_pagar: 0, hoy: 0, proximas: 0, en_curso: 0, con_incidencia: 0, historial: 0 } as Record<PestanaReservas, number>;
  for (const f of filas) for (const p of PESTANAS_RESERVAS) if (enPestana(f, p.id, hoy)) c[p.id] += 1;
  return c;
}

export type CampoBusquedaReserva = "folio" | "unidad" | "solicitante";

const plano = (v: string | null | undefined): string =>
  (v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** ¿La fila coincide con lo buscado en los campos pedidos? (sin acentos ni mayúsculas). */
export function coincideReserva(f: Pick<ReservaFila, "folio" | "unidad" | "solicitante">, texto: string, campos: readonly CampoBusquedaReserva[]): boolean {
  const q = plano(texto);
  if (!q) return true;
  return campos.some((c) => plano(f[c]).includes(q));
}

export type TipoPrioridad = "apartado" | "checkin" | "checkout";

export interface PrioridadRecepcion {
  tipo: TipoPrioridad;
  id: number;
  folio: string;
  titulo: string;
  detalle: string;
  /** Para ordenar: cuándo hay que atenderla. */
  cuando: string;
}

/**
 * Lo que recepción tiene que atender pronto: apartados que vencen en las próximas 6 h, check-in
 * de las confirmadas de hoy que empiezan en la próxima hora (o ya empezaron) y check-out de las
 * en curso que ya terminaron. Ordenado por hora; a lo más `max`.
 */
export function prioridadesRecepcion(filas: ReservaFila[], ahora = new Date(), max = 5): PrioridadRecepcion[] {
  const t = ahora.getTime();
  const hoy = hoyCdmx(ahora);
  const out: PrioridadRecepcion[] = [];
  for (const f of filas) {
    const lugar = `${f.amenidad} - ${f.espacio}`;
    if (f.estado === "por_pagar" && f.apartado_hasta) {
      const vence = new Date(f.apartado_hasta).getTime();
      if (vence > t && vence - t <= 6 * 3600_000) {
        out.push({ tipo: "apartado", id: f.id, folio: f.folio, titulo: `${f.folio} - ${lugar}`, detalle: `Apartado vence a las ${horaCdmx(f.apartado_hasta)}. ${f.unidad}.`, cuando: f.apartado_hasta });
      }
    } else if (f.estado === "confirmada" && fechaCdmx(f.inicio) === hoy) {
      const ini = new Date(f.inicio).getTime();
      if (ini - t <= 3600_000 && new Date(f.fin).getTime() > t) {
        out.push({ tipo: "checkin", id: f.id, folio: f.folio, titulo: `${f.folio} - ${lugar}`, detalle: `Check-in pendiente (${rangoHoras(f.inicio, f.fin)}). ${f.unidad}.`, cuando: f.inicio });
      }
    } else if (f.estado === "en_curso" && new Date(f.fin).getTime() <= t) {
      out.push({ tipo: "checkout", id: f.id, folio: f.folio, titulo: `${f.folio} - ${lugar}`, detalle: `Terminó a las ${horaCdmx(f.fin)}: falta el check-out.`, cuando: f.fin });
    }
  }
  return out.sort((a, b) => a.cuando.localeCompare(b.cuando)).slice(0, max);
}

// ---------------------------------------------------------------------------------------------
// Avance de la reserva (PasosHorizontales)
// ---------------------------------------------------------------------------------------------

export type EstadoPasoReserva = "hecho" | "actual" | "pendiente";

/** Por pagar - Confirmada - En curso - Cerrada. Una reserva sin costo se salta el primero. */
export function pasosReserva(estado: EstadoReserva, sinCosto = false): { id: EstadoReserva; titulo: string; estado: EstadoPasoReserva }[] {
  const orden: EstadoReserva[] = ["por_pagar", "confirmada", "en_curso", "cerrada"];
  const actual = estado === "cancelada" ? -1 : orden.indexOf(estado);
  return orden.map((id, i) => ({
    id,
    titulo: id === "por_pagar" && sinCosto ? "Sin costo" : ESTADO_RESERVA[id].label,
    estado: estado === "cerrada" || i < actual ? "hecho" : i === actual ? "actual" : "pendiente",
  }));
}

// ---------------------------------------------------------------------------------------------
// Lista de verificación para publicar (la misma que valida `amen_publicar` en el servidor)
// ---------------------------------------------------------------------------------------------

export type BorradorAmenidad = Pick<AmenidadAmen,
  "nombre" | "reservable" | "modelo_cobro" | "tarifa" | "duracion_max_min" | "duracion_min_min" | "capacidad_max"
  | "horario_apertura" | "horario_cierre" | "politica_cancelacion"
>;

/**
 * Pendientes para publicar. Un campo vacío nunca es cero ni ilimitado (DISENO §3): se pide.
 * Ficha informativa (no reservable): solo nombre y portada.
 */
export function checklistAmenidad(
  a: BorradorAmenidad,
  extra: { reglamentoVigente: boolean; espaciosActivos: number; conPortada: boolean },
): ItemChecklist[] {
  const items: ItemChecklist[] = [{ clave: "nombre", ok: a.nombre.trim().length > 0, texto: "Nombre de la amenidad" }];
  if (a.reservable) {
    const cobra = a.modelo_cobro !== "gratuito";
    if (cobra) items.push({ clave: "tarifa", ok: (a.tarifa ?? 0) > 0, texto: a.modelo_cobro === "por_hora" ? "Tarifa por hora" : "Tarifa por reservación" });
    items.push({ clave: "duracion", ok: (a.duracion_max_min ?? 0) >= (a.duracion_min_min || 1), texto: "Duración máxima por reservación" });
    items.push({ clave: "capacidad", ok: (a.capacidad_max ?? 0) > 0, texto: "Capacidad máxima de personas" });
    const horarioOk = !!a.horario_apertura && !!a.horario_cierre
      && (minutosDeHora(a.horario_cierre) === 0 || minutosDeHora(a.horario_cierre) > minutosDeHora(a.horario_apertura));
    items.push({ clave: "horario", ok: horarioOk, texto: "Horario de apertura y cierre" });
    if (cobra) items.push({ clave: "politica", ok: !!a.politica_cancelacion, texto: "Política de cancelación" });
    items.push({ clave: "reglamento", ok: extra.reglamentoVigente, texto: "Reglamento vigente" });
    items.push({ clave: "espacios", ok: extra.espaciosActivos > 0, texto: "Al menos un espacio activo" });
  }
  items.push({ clave: "portada", ok: extra.conPortada, texto: "Foto de portada" });
  return items;
}

export const pendientesChecklist = (items: ItemChecklist[]): number => items.filter((i) => !i.ok).length;

// ---------------------------------------------------------------------------------------------
// Textos del catálogo
// ---------------------------------------------------------------------------------------------

/** "$350.00 / hora", "$1,000.00 / reservación" o "Sin costo". */
export function textoTarifa(a: Pick<AmenidadAmen, "modelo_cobro" | "tarifa">): string {
  if (a.modelo_cobro === "gratuito") return "Sin costo";
  if (a.tarifa == null) return "Tarifa sin capturar";
  return `${pesos(a.tarifa)} / ${a.modelo_cobro === "por_hora" ? "hora" : "reservación"}`;
}

/** "08:00 a 23:00" o "Horario sin capturar". */
export const textoHorario = (a: Pick<AmenidadAmen, "horario_apertura" | "horario_cierre">): string =>
  a.horario_apertura && a.horario_cierre ? `${a.horario_apertura} a ${a.horario_cierre}` : "Horario sin capturar";

/** "2 espacios - Sala A y Sala B" (activos primero). */
export function textoEspacios(espacios: Pick<EspacioAmen, "nombre" | "activo">[]): string {
  const activos = espacios.filter((e) => e.activo);
  if (activos.length === 0) return "Sin espacios";
  const nombres = activos.map((e) => e.nombre);
  const unidos = nombres.length <= 1 ? nombres.join("") : `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
  return `${activos.length} ${activos.length === 1 ? "espacio" : "espacios"} - ${unidos}`;
}

/** Error de una acción en palabras (DETAIL del servidor, código del contrato o genérico). */
export function mensajeErrorAmen(e: unknown, generico = "No se pudo completar la acción. Intenta de nuevo."): string {
  if (!e || typeof e !== "object") return generico;
  const { code, message, details, mensaje } = e as { code?: string; message?: string; details?: string; mensaje?: string };
  // Edge: `{error, mensaje}` (el texto ya viene listo). RPC: el texto va en el DETAIL.
  if (mensaje) return mensaje;
  const clave = (message ?? "").trim();
  if (details && (code === "P0001" || clave in ERRORES_AMEN)) return details;
  if (code === "42501") return ERRORES_AMEN.sin_permiso!;
  return ERRORES_AMEN[clave] ?? ERRORES_AMEN[code ?? ""] ?? generico;
}
